import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useModifyComponentBlueprintDefinition } from '../../../../../api/denali'
import OpalCard from '../../../../opal/OpalCard'
import InlineCardHeader from '../../../../molecules/Card/InlineCardHeader'
import SydButton from '../../../../commonDesign/Button'
import {
  ComponentListSidebar,
  ComponentDetailPanel,
  AddComponentDialog
} from './componentManagement'

function ComponentsTab ({ blueprint, onSave }) {
  const { mutateAsync: modifyDefinition } = useModifyComponentBlueprintDefinition()
  const addComponentRef = useRef(null)

  const serverPresentations = blueprint.definition?.data?.presentations || {}
  const [localPresentations, setLocalPresentations] = useState(serverPresentations)
  const [selectedCode, setSelectedCode] = useState(() => {
    const codes = Object.keys(serverPresentations)
    return codes[0] || null
  })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setLocalPresentations(blueprint.definition?.data?.presentations || {})
  }, [blueprint.definition])

  const isDirty = useMemo(() => {
    const original = JSON.stringify(blueprint.definition?.data?.presentations || {})
    const current = JSON.stringify(localPresentations)
    return original !== current
  }, [localPresentations, blueprint.definition])

  const handleAddComponent = useCallback(({ code, data }) => {
    setLocalPresentations(prev => ({
      ...prev,
      [code]: data
    }))
    setSelectedCode(code)
  }, [])

  const handleRemoveComponent = useCallback((code) => {
    const entry = localPresentations[code]
    const confirmed = window.confirm(`Remove component "${entry?.displayName || code}"?`)
    if (confirmed) {
      setLocalPresentations(prev => {
        const updated = { ...prev }
        delete updated[code]
        return updated
      })
      if (selectedCode === code) {
        const remaining = Object.keys(localPresentations).filter(k => k !== code)
        setSelectedCode(remaining[0] || null)
      }
    }
  }, [selectedCode, localPresentations])

  const handleUpdateConfig = useCallback((code, updatedConfig) => {
    setLocalPresentations(prev => ({
      ...prev,
      [code]: {
        ...prev[code],
        config: updatedConfig
      }
    }))
  }, [])

  const handleUpdateOption = useCallback((code, field, value) => {
    setLocalPresentations(prev => ({
      ...prev,
      [code]: {
        ...prev[code],
        [field]: value
      }
    }))
  }, [])

  const handleSave = useCallback(async () => {
    try {
      setSaving(true)
      await modifyDefinition({
        blueprintId: blueprint.blueprintId,
        data: { presentations: localPresentations }
      })
      onSave()
    } catch (err) {
      console.error('Failed to save components:', err)
    } finally {
      setSaving(false)
    }
  }, [blueprint.blueprintId, localPresentations, modifyDefinition, onSave])

  return (
    <OpalCard size='sm'>
      <InlineCardHeader
        action={
          isDirty ? (
            <SydButton
              onClick={handleSave}
              processing={saving}
              variant='primary'
              size='sm'
              icon='check'
            >
              Save Changes
            </SydButton>
          ) : null
        }
      >
        Components
      </InlineCardHeader>
      <div style={{ padding: '16px' }}>
        {blueprint.definition ? (
          <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '16px', minHeight: '400px' }}>
            <ComponentListSidebar
              presentations={localPresentations}
              selectedCode={selectedCode}
              onSelect={setSelectedCode}
              onAdd={() => addComponentRef.current.open()}
              onRemove={handleRemoveComponent}
            />
            <ComponentDetailPanel
              code={selectedCode}
              data={localPresentations[selectedCode]}
              onUpdateConfig={handleUpdateConfig}
              onUpdateOption={handleUpdateOption}
            />
          </div>
        ) : (
          <p style={{ color: 'var(--color-neutral-500)' }}>
            No active definition. Add a version from the Settings tab to get started.
          </p>
        )}
      </div>

      <AddComponentDialog ref={addComponentRef} onComplete={handleAddComponent} />
    </OpalCard>
  )
}

ComponentsTab.propTypes = {
  blueprint: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired
}

export default ComponentsTab
